import { useEffect, useRef, useState } from "react";
import { formRevision as formRevisionOf, type FormDefinition } from "@dayvise/contracts";
import { Badge } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { Select, Switch, TextArea, TextField } from "../primitives/Fields.tsx";
import { LABEL_TEXT, MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import {
  FORM_SNOOZE_MS,
  buildFormSubmission,
  formDraftIsEmpty,
  formFieldAutoComplete,
  formFieldValueLabel,
  formProblems,
  initialFormDrafts,
  protectedFieldCount,
  snoozeIsActive,
  snoozeRemainingLabel,
  type FormDrafts,
  type FormFieldDraft,
  type FormFieldProblem,
  type FormSubmission,
} from "../logic/forms.ts";

export interface FormPromptProps {
  readonly clarificationId: string;
  readonly form: FormDefinition;
  /** Revision the client was shown; a different computed revision means the form changed. */
  readonly formRevision: string;
  readonly fieldErrors?: readonly FormFieldProblem[];
  readonly busy?: boolean;
  readonly error?: string | null;
  /** Set when the server refused the submission because the form changed. */
  readonly staleFromServer?: boolean;
  /** Changing this value (account, conversation, run) discards the local draft. */
  readonly draftKey?: string;
  readonly onSubmit: (submission: FormSubmission) => Promise<void> | void;
  readonly onAnswerWithoutAudio?: (submission: FormSubmission) => Promise<void> | void;
  readonly onDecline?: () => Promise<void> | void;
  readonly onSnooze?: () => Promise<void> | void;
  readonly onRefresh?: () => Promise<void> | void;
}

const EMPTY_DRAFT: FormFieldDraft = { value: "", protect: false, plainConfirmed: false };

export function FormPrompt({
  clarificationId,
  form,
  formRevision,
  fieldErrors = [],
  busy = false,
  error = null,
  staleFromServer = false,
  draftKey = "",
  onSubmit,
  onAnswerWithoutAudio,
  onDecline,
  onSnooze,
  onRefresh,
}: FormPromptProps) {
  const identity = `${clarificationId}:${formRevision}:${draftKey}`;
  const latest = useRef(form);
  latest.current = form;
  const [drafts, setDrafts] = useState<FormDrafts>(() => initialFormDrafts(form));
  const [attempted, setAttempted] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  const controls = useRef(new Map<string, HTMLElement | null>());
  const current = useRef(identity);

  useEffect(() => {
    current.current = identity;
    setAttempted(false);
    setSnoozedUntil(null);
    setDrafts(initialFormDrafts(latest.current));
  }, [identity]);

  useEffect(() => {
    if (!snoozeIsActive(snoozedUntil)) return;
    const timer = setTimeout(() => setSnoozedUntil(null), Math.max(0, (snoozedUntil ?? 0) - Date.now()));
    return () => clearTimeout(timer);
  }, [snoozedUntil]);

  const stale = staleFromServer || formRevisionOf(form, clarificationId) !== formRevision;
  const snoozed = snoozeIsActive(snoozedUntil);
  const problems = formProblems(form, drafts);
  const problemFor = (fieldId: string): string | null => {
    const local = attempted ? problems.find((problem) => problem.fieldId === fieldId) : undefined;
    return local?.message ?? fieldErrors.find((problem) => problem.fieldId === fieldId)?.message ?? null;
  };
  const update = (fieldId: string, change: Partial<FormFieldDraft>) => {
    setDrafts((previous) => ({ ...previous, [fieldId]: { ...(previous[fieldId] ?? EMPTY_DRAFT), ...change } }));
  };
  const send = (handler: (submission: FormSubmission) => Promise<void> | void) => {
    setAttempted(true);
    if (stale || snoozed) return;
    const found = formProblems(latest.current, drafts);
    if (found.length > 0) {
      focusField(found[0]!.fieldId);
      return;
    }
    const submission = buildFormSubmission(latest.current, drafts, clarificationId);
    void Promise.resolve(handler(submission)).catch(() => undefined);
  };
  const register = (fieldId: string) => (node: HTMLElement | null) => { controls.current.set(fieldId, node); };
  const focusField = (fieldId: string) => {
    const container = controls.current.get(fieldId);
    container?.querySelector<HTMLElement>("input:not([type=hidden]), select, textarea, button")?.focus() ?? container?.focus();
  };
  const protectedCount = protectedFieldCount(form, drafts);

  return (
    <Card title={form.title} subtitle={form.description ?? "The agent needs a structured answer before it continues."}>
      <div style={stackStyle("var(--dayvise-space-md)")}>
        {snoozed ? (
          <div style={rowStyle("var(--dayvise-space-xs)", "space-between")}>
            <span role="status" style={MUTED_TEXT}>
              Snoozed for {snoozeRemainingLabel(snoozedUntil ?? 0)}. The request stays pending for this run.
            </span>
            <Button size="sm" variant="ghost" onClick={() => setSnoozedUntil(null)}>Show now</Button>
          </div>
        ) : null}
        {stale ? (
          <p role="alert" style={{ ...MUTED_TEXT, margin: 0 }}>
            This form changed after it was shown, so it is no longer safe to answer here.
            {onRefresh ? <Button size="sm" variant="secondary" onClick={() => void Promise.resolve(onRefresh()).catch(() => undefined)}>Refresh the form</Button> : null}
          </p>
        ) : null}
        {snoozed ? null : form.fields.map((field, index) => {
          const draft = drafts[field.id] ?? EMPTY_DRAFT;
          const problem = problemFor(field.id);
          const describedBy = [`form-${clarificationId}-${field.id}-kind`];
          if (problem !== null) describedBy.push(`form-${clarificationId}-${field.id}-error`);
          const kind = `${formFieldValueLabel(field)}${field.required === true ? ", required" : ", optional"}`;
          const locked = busy || stale;
          return (
            <div key={field.id} ref={register(field.id)} tabIndex={-1} style={{ ...stackStyle("var(--dayvise-space-xs)"), paddingBottom: index < form.fields.length - 1 ? "var(--dayvise-space-sm)" : 0, borderBottom: index < form.fields.length - 1 ? "1px solid var(--dayvise-color-border)" : "none" }}>
              <span id={`form-${clarificationId}-${field.id}-kind`} style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{kind}</span>
              {field.type === "text" ? (
                <TextField
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  optional={field.required !== true}
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  value={String(draft.value ?? "")}
                  maxLength={field.maxLength}
                  disabled={locked || draft.protect}
                  autoComplete="off"
                  aria-describedby={describedBy.join(" ")}
                  onChange={(event) => update(field.id, { value: event.target.value })}
                />
              ) : null}
              {field.type === "multiline" ? (
                <TextArea
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  optional={field.required !== true}
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  rows={4}
                  value={String(draft.value ?? "")}
                  maxLength={field.maxLength}
                  disabled={locked || draft.protect}
                  aria-describedby={describedBy.join(" ")}
                  onChange={(event) => update(field.id, { value: event.target.value })}
                />
              ) : null}
              {field.type === "secret" ? (
                <TextField
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  optional={field.required !== true}
                  secret
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  value={String(draft.value ?? "")}
                  disabled={locked}
                  autoComplete={formFieldAutoComplete(field)}
                  aria-describedby={describedBy.join(" ")}
                  onChange={(event) => update(field.id, { value: event.target.value })}
                />
              ) : null}
              {field.type === "select" ? (
                <Select
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  value={String(draft.value ?? "")}
                  disabled={locked}
                  aria-describedby={describedBy.join(" ")}
                  options={[{ value: "", label: "Choose…" }, ...field.options.map((option) => ({ value: option.id, label: option.label }))]}
                  onChange={(event) => update(field.id, { value: event.target.value })}
                />
              ) : null}
              {field.type === "choice" ? (
                <fieldset style={{ border: "none", margin: 0, padding: 0, ...stackStyle("var(--dayvise-space-xxs)") }} aria-invalid={problem !== null} aria-describedby={describedBy.join(" ")}>
                  <legend style={LABEL_TEXT}>{field.label}</legend>
                  {field.helper ? <span style={MUTED_TEXT}>{field.helper}</span> : null}
                  {field.options.map((option) => (
                    <label key={option.id} style={{ ...rowStyle("var(--dayvise-space-xs)"), cursor: "pointer", fontSize: "var(--dayvise-font-size-sm)" }}>
                      <input
                        type="radio"
                        name={field.id}
                        value={option.id}
                        checked={draft.value === option.id}
                        disabled={locked}
                        onChange={() => update(field.id, { value: option.id })}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </fieldset>
              ) : null}
              {field.type === "multichoice" ? (
                <fieldset style={{ border: "none", margin: 0, padding: 0, ...stackStyle("var(--dayvise-space-xxs)") }} aria-invalid={problem !== null} aria-describedby={describedBy.join(" ")}>
                  <legend style={LABEL_TEXT}>{field.label}</legend>
                  {field.helper ? <span style={MUTED_TEXT}>{field.helper}</span> : null}
                  {field.options.map((option) => {
                    const selected = Array.isArray(draft.value) ? draft.value : [];
                    return (
                      <label key={option.id} style={{ ...rowStyle("var(--dayvise-space-xs)"), cursor: "pointer", fontSize: "var(--dayvise-font-size-sm)" }}>
                        <input
                          type="checkbox"
                          name={field.id}
                          value={option.id}
                          checked={selected.includes(option.id)}
                          disabled={locked}
                          onChange={() => update(field.id, { value: selected.includes(option.id) ? selected.filter((entry) => entry !== option.id) : [...selected, option.id] })}
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}
              {field.type === "boolean" ? (
                <div aria-describedby={describedBy.join(" ")}>
                  <Switch label={field.label} description={field.helper} checked={draft.value === true} disabled={locked} onChange={(checked) => update(field.id, { value: checked })} />
                </div>
              ) : null}
              {field.type === "number" ? (
                <TextField
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  optional={field.required !== true}
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  type="number"
                  inputMode="numeric"
                  value={String(draft.value ?? "")}
                  min={field.min}
                  max={field.max}
                  step={field.integer === true ? 1 : "any"}
                  disabled={locked}
                  aria-describedby={describedBy.join(" ")}
                  onChange={(event) => update(field.id, { value: event.target.value === "" ? "" : Number(event.target.value) })}
                />
              ) : null}
              {field.type === "date" ? (
                <TextField
                  label={field.label}
                  hint={field.helper}
                  error={problem}
                  optional={field.required !== true}
                  id={`form-${clarificationId}-${field.id}`}
                  name={field.id}
                  type="date"
                  value={String(draft.value ?? "")}
                  disabled={locked}
                  aria-describedby={describedBy.join(" ")}
                  onChange={(event) => update(field.id, { value: event.target.value })}
                />
              ) : null}
              {problem !== null ? <span id={`form-${clarificationId}-${field.id}-error`} role="alert" style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-xs)" }}>{problem}</span> : null}
              {field.type !== "secret" && (field.type === "text" || field.type === "multiline") ? (
                <Switch
                  label="Protect this answer"
                  description="Seal the value before it leaves this client. The agent sees a handle, never the text."
                  checked={draft.protect}
                  disabled={locked}
                  onChange={(checked) => update(field.id, { value: draft.protect && !checked ? "" : draft.value, protect: checked, plainConfirmed: false })}
                />
              ) : null}
              {field.type !== "secret" && (field.type === "text" || field.type === "multiline") && !draft.protect ? (
                <Switch
                  label="This is not a credential"
                  description="Confirm the wording is misleading and this value may be sent as ordinary text."
                  checked={draft.plainConfirmed}
                  disabled={locked}
                  onChange={(checked) => update(field.id, { plainConfirmed: checked })}
                />
              ) : null}
              {field.type === "secret" && !formDraftIsEmpty(field, draft) ? <Badge tone="neutral">Value sealed on submit</Badge> : null}
            </div>
          );
        })}

        {error !== null ? <p role="alert">{error}</p> : null}
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>
          {protectedCount > 0
            ? `${protectedCount} answer(s) will be sealed before submission; the agent only receives a handle.`
            : "Prefilled options are not answers: nothing is submitted until you press submit."}
        </span>
        <div style={rowStyle("var(--dayvise-space-xs)", "flex-end")}>
          {onDecline ? <Button size="sm" variant="ghost" disabled={busy} onClick={() => void Promise.resolve(onDecline()).catch(() => undefined)}>Decline</Button> : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || snoozed}
            onClick={() => {
              setSnoozedUntil(Date.now() + FORM_SNOOZE_MS);
              if (onSnooze) void Promise.resolve(onSnooze()).catch(() => undefined);
            }}
          >
            Snooze
          </Button>
          {onAnswerWithoutAudio ? <Button size="sm" variant="secondary" disabled={busy || stale || snoozed} onClick={() => send(onAnswerWithoutAudio)}>Submit without audio</Button> : null}
          <Button variant="primary" size="sm" loading={busy} disabled={stale || snoozed} onClick={() => send(onSubmit)}>{form.submitLabel ?? "Submit"}</Button>
        </div>
      </div>
    </Card>
  );
}
